/**
 * ImportProgress - Composant d'affichage de la progression d'un import
 * Design System: Dashboard context (bg-white, text-gray-900)
 */

import { useEffect } from 'react';
import {
  Loader2,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Clock,
  FileText,
  Download,
  RotateCcw,
  Play,
} from 'lucide-react';
import type { Import, ImportStatus } from '../types/import.types';
import { useImportStatus } from '../hooks/useImport';

interface ImportProgressProps {
  importId: string;
  onComplete?: (result: Import) => void;
  onDownload?: () => void;
  onRollback?: () => void;
  onStartProcess?: () => void;
  isStartingProcess?: boolean;
}

/**
 * Mapping des clés de resultSummary vers des libellés français lisibles
 */
const SUMMARY_KEY_LABELS: Record<string, string> = {
  entriesCreated: 'Écritures créées',
  linesCreated: 'Lignes créées',
  accountsCreated: 'Comptes créés',
  success: 'Succès',
  invoicesCreated: 'Factures créées',
  clientsCreated: 'Clients créés',
  productsCreated: 'Produits créés',
  transactionsCreated: 'Transactions créées',
  transactionsSkipped: 'Transactions ignorées',
  itemsCreated: 'Éléments créés',
  errors: 'Erreurs',
  warnings: 'Avertissements',
  processed: 'Traités',
  skipped: 'Ignorés',
  totalDebit: 'Total débit',
  totalCredit: 'Total crédit',
};

/**
 * Formate une clé de resultSummary en libellé lisible
 */
function formatSummaryKey(key: string): string {
  if (SUMMARY_KEY_LABELS[key]) {
    return SUMMARY_KEY_LABELS[key];
  }
  // Convertir camelCase en libellé lisible
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
}

/**
 * Formate une valeur de resultSummary pour l'affichage
 * - Les tableaux affichent leur longueur
 * - Les booléens affichent Oui/Non
 * - Les nombres sont formatés avec séparateur de milliers
 */
function formatSummaryValue(value: unknown): string {
  if (Array.isArray(value)) {
    return String(value.length);
  }
  if (typeof value === 'boolean') {
    return value ? 'Oui' : 'Non';
  }
  if (typeof value === 'number') {
    return value.toLocaleString('fr-FR');
  }
  if (value === null || value === undefined) {
    return '-';
  }
  return String(value);
}

const STATUS_CONFIG: Record<
  ImportStatus,
  { label: string; icon: typeof Loader2; color: string; bgColor: string }
> = {
  PENDING: { label: 'Traitement en file d\'attente...', icon: Loader2, color: 'text-teal-500', bgColor: 'bg-teal-100' },
  UPLOADING: { label: 'Upload en cours', icon: Loader2, color: 'text-blue-500', bgColor: 'bg-blue-100' },
  UPLOADED: { label: 'Fichier uploadé', icon: CheckCircle, color: 'text-blue-500', bgColor: 'bg-blue-100' },
  PARSING: { label: 'Analyse en cours', icon: Loader2, color: 'text-blue-500', bgColor: 'bg-blue-100' },
  PARSED: { label: 'Fichier analysé', icon: CheckCircle, color: 'text-blue-500', bgColor: 'bg-blue-100' },
  VALIDATING: { label: 'Validation en cours', icon: Loader2, color: 'text-amber-500', bgColor: 'bg-amber-100' },
  VALIDATED: { label: 'Données validées', icon: CheckCircle, color: 'text-amber-500', bgColor: 'bg-amber-100' },
  AWAITING_CONFIRM: { label: 'En attente de confirmation', icon: Clock, color: 'text-amber-500', bgColor: 'bg-amber-100' },
  PROCESSING: { label: 'Traitement en cours', icon: Loader2, color: 'text-teal-500', bgColor: 'bg-teal-100' },
  COMPLETED: { label: 'Import terminé', icon: CheckCircle, color: 'text-green-500', bgColor: 'bg-green-100' },
  COMPLETED_PARTIAL: { label: 'Import partiel', icon: AlertTriangle, color: 'text-amber-500', bgColor: 'bg-amber-100' },
  FAILED: { label: 'Échec', icon: XCircle, color: 'text-red-500', bgColor: 'bg-red-100' },
  CANCELLED: { label: 'Annulé', icon: XCircle, color: 'text-gray-500', bgColor: 'bg-gray-100' },
  ROLLED_BACK: { label: 'Annulé (rollback)', icon: RotateCcw, color: 'text-gray-500', bgColor: 'bg-gray-100' },
};

export function ImportProgress({
  importId,
  onComplete,
  onDownload,
  onRollback,
  onStartProcess,
  isStartingProcess = false,
}: ImportProgressProps) {
  // Poll for status updates while processing
  const { data: importData, isLoading } = useImportStatus(importId, {
    refetchInterval: 2000,
  });

  // Notify parent when complete
  useEffect(() => {
    if (importData && ['COMPLETED', 'COMPLETED_PARTIAL', 'FAILED'].includes(importData.status)) {
      onComplete?.(importData);
    }
  }, [importData, onComplete]);

  if (isLoading || !importData) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-8 h-8 text-teal-600 animate-spin" />
      </div>
    );
  }

  const statusConfig = STATUS_CONFIG[importData.status] || STATUS_CONFIG.PENDING;
  const StatusIcon = statusConfig.icon;
  // PENDING = job en queue, PROCESSING = job en cours d'exécution
  const isProcessing = ['UPLOADING', 'PARSING', 'VALIDATING', 'PROCESSING', 'PENDING'].includes(importData.status);
  const isComplete = ['COMPLETED', 'COMPLETED_PARTIAL'].includes(importData.status);
  const isFailed = importData.status === 'FAILED';
  // Ne montrer le bouton "Lancer" que si VALIDATED ou AWAITING_CONFIRM (pas PENDING car déjà en queue)
  const isAwaitingProcess = ['VALIDATED', 'AWAITING_CONFIRM'].includes(importData.status);

  return (
    <div className="space-y-6">
      {/* Status Header */}
      <div className={`p-6 rounded-xl ${statusConfig.bgColor}`}>
        <div className="flex items-center gap-4">
          <div className={`p-3 rounded-full bg-white ${statusConfig.color}`}>
            <StatusIcon className={`w-6 h-6 ${isProcessing ? 'animate-spin' : ''}`} />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{statusConfig.label}</h3>
            {importData.currentStep && (
              <p className="text-sm text-gray-600">{importData.currentStep}</p>
            )}
          </div>
        </div>

        {/* Progress Bar */}
        {isProcessing && importData.progress > 0 && (
          <div className="mt-4">
            <div className="flex justify-between text-sm text-gray-600 mb-1">
              <span>Progression</span>
              <span>{Math.round(importData.progress)}%</span>
            </div>
            <div className="h-2 bg-white rounded-full overflow-hidden">
              <div
                className="h-full bg-teal-500 transition-all duration-300"
                style={{ width: `${importData.progress}%` }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Start Process Button - Shown when import is validated but not yet processing */}
      {isAwaitingProcess && onStartProcess && (
        <div className="flex flex-col items-center justify-center p-6 bg-teal-50 border border-teal-200 rounded-xl">
          <p className="text-gray-700 mb-4 text-center">
            Les données ont été validées. Vous pouvez maintenant lancer le traitement pour importer les données.
          </p>
          <button
            onClick={onStartProcess}
            disabled={isStartingProcess}
            className="flex items-center gap-2 px-6 py-3 bg-teal-600 text-white rounded-xl font-medium hover:bg-teal-700 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:shadow-none"
          >
            {isStartingProcess ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Lancement en cours...
              </>
            ) : (
              <>
                <Play className="w-5 h-5" />
                Lancer le traitement
              </>
            )}
          </button>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="p-4 bg-gray-50 rounded-lg">
          <div className="text-sm text-gray-500">Total lignes</div>
          <div className="text-2xl font-semibold text-gray-900">{importData.totalRows}</div>
        </div>
        <div className="p-4 bg-gray-50 rounded-lg">
          <div className="text-sm text-gray-500">Traitées</div>
          <div className="text-2xl font-semibold text-gray-900">{importData.processedRows}</div>
        </div>
        <div className="p-4 bg-green-50 rounded-lg">
          <div className="text-sm text-green-600">Succès</div>
          <div className="text-2xl font-semibold text-green-700">{importData.successCount}</div>
        </div>
        <div className="p-4 bg-red-50 rounded-lg">
          <div className="text-sm text-red-600">Erreurs</div>
          <div className="text-2xl font-semibold text-red-700">{importData.errorCount}</div>
        </div>
      </div>

      {/* Error Message */}
      {isFailed && importData.errorMessage && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start gap-3">
            <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-medium text-red-800">Erreur lors de l'import</h4>
              <p className="text-sm text-red-700 mt-1">{importData.errorMessage}</p>
            </div>
          </div>
        </div>
      )}

      {/* Result Summary */}
      {isComplete && importData.resultSummary && (
        <div className="p-4 bg-teal-50 border border-teal-200 rounded-lg">
          <h4 className="font-medium text-gray-900 mb-2">Résumé de l'import</h4>
          <div className="text-sm text-gray-600 space-y-1">
            {Object.entries(importData.resultSummary)
              .filter(([key]) => !key.endsWith('Ids') && !key.endsWith('ids'))
              .map(([key, value]) => (
                <div key={key} className="flex justify-between">
                  <span>{formatSummaryKey(key)}:</span>
                  <span className="font-medium text-gray-900">{formatSummaryValue(value)}</span>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* File Info */}
      <div className="p-4 bg-gray-50 rounded-lg flex items-center justify-between">
        <div className="flex items-center gap-3">
          <FileText className="w-5 h-5 text-gray-400" />
          <div>
            <p className="text-sm font-medium text-gray-900">{importData.originalFileName || 'Fichier importé'}</p>
            <p className="text-xs text-gray-500">
              {importData.originalFileSize
                ? `${(importData.originalFileSize / 1024).toFixed(1)} KB`
                : 'Taille inconnue'}
            </p>
          </div>
        </div>
        {onDownload && (
          <button
            onClick={onDownload}
            className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
            title="Télécharger le fichier original"
          >
            <Download className="w-5 h-5 text-gray-500" />
          </button>
        )}
      </div>

      {/* Actions */}
      {isComplete && importData.canRollback && onRollback && (
        <div className="flex justify-end">
          <button
            onClick={onRollback}
            className="flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
          >
            <RotateCcw className="w-4 h-4" />
            Annuler l'import (rollback)
          </button>
        </div>
      )}

      {/* Timestamps */}
      <div className="text-xs text-gray-400 space-y-1">
        {importData.createdAt && (
          <p>Créé le: {new Date(importData.createdAt).toLocaleString('fr-FR')}</p>
        )}
        {importData.startedAt && (
          <p>Démarré le: {new Date(importData.startedAt).toLocaleString('fr-FR')}</p>
        )}
        {importData.completedAt && (
          <p>Terminé le: {new Date(importData.completedAt).toLocaleString('fr-FR')}</p>
        )}
      </div>
    </div>
  );
}
